import { memo, useEffect, useState, useCallback, useRef, useMemo } from "react";
import { Box, Typography, Divider, Stack, TextField, FormControl, FormLabel, Button, Drawer, Chip, FormHelperText, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { useCreateFilter, useSearchFilterData, useGetFilters, useDeleteFilter } from '../../api/vegApl';
import type { GetFiltersModel } from '../../api/vegApl';
import { useCommonStyles } from '../../styles/GenericStyle';
import { black } from '../../colors';
import { FilterConFig } from '../../utils/FilterConfig';
import { useGlobalState } from "../../contexts/GlobalState";
import { useSnackbar } from '../../contexts/SnackbarContext';
import CloseIcon from '@mui/icons-material/Close';
import { MtComStyleObj } from '../../styles/MtComStyleObj';
import IconButton from '@mui/material/IconButton';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import type { ModuleFilterStrategy, FilterRequestContext } from './filterStrategy';
import { defaultFilterStrategy } from './filterStrategy';
import type { PeoplePickerPerson } from '../../model/commonType';
import { useFilterPanelPersistence } from '../../hooks/usePersistedGridState';

// Re-export types for backwards compatibility
export type { FieldType, SelectOption, FilterFieldDefinition, FilterConfig } from './filterPanel';
import type { GenericFilterPanelProps } from './filterPanel';
import { restoreDateRanges, checkDate, mapFilterToFormData, buildChipDescriptors, getFieldResetValue } from './filterPanel';
import { drawerSx, drawerBackdropSx, chipSx, accordionSummaryExpandedSx } from './filterPanel';
import { FilterFieldRenderer } from './filterPanel';
// import { GenericConstant } from "../../utils/GenericConstant";

const resolveList = (field: any, listValues: any): any[] =>
    Array.isArray(field?.list) ? field.list : (listValues?.[field?.list] ?? []);

function GenericFilterPanel({ moduleName, listValues, setGridData, showArchived = false, onRefreshData, paginationModel, setTotalRows, onFilterApplied, isFilterVisible, setFilterVisible, setFilterFieldData, accessibleFilterTab, filtetToDelete, extraContext, extraBody, searchTrigger, persistState = true }: GenericFilterPanelProps) {
    const classes = useCommonStyles();
    const [formData, setFormData] = useState<Record<string, any>>({});
    // Snapshot of formData at the last applied/restored filter; drives the external filter chips.
    const [appliedFormData, setAppliedFormData] = useState<Record<string, any>>({});
    const commitFormData = useCallback((data: Record<string, any>) => {
        setFormData(data);
        setAppliedFormData(data);
    }, []);
    const appliedFormDataRef = useRef<Record<string, any>>({});
    appliedFormDataRef.current = appliedFormData;
    const filterConfig = FilterConFig[moduleName];
    const fields = filterConfig["fields"];
    const sortedFields = useMemo(() =>
        [...fields].sort((a: any, b: any) => (a.label ?? '').localeCompare(b.label ?? '')),
        [fields]
    );
    const { showSnackbar } = useSnackbar();
    const [allSavedFilters, setAllSaveFilter] = useState<GetFiltersModel[]>([]);
    const [currentFilterSelected, setCurrentFilterSelected] = useState(-1);
    const [filterNameValidMsg, setFilterNameValidMsg] = useState("");
    const [dateValidMsg, setDateValidMsg] = useState("");
    const { state: globalState, state: { siteSelected } = {} } = useGlobalState();
    const filterNameKey = filterConfig["filter_name_key"] as string;
    const [invokeSearch, setInvokeSearch] = useState(false);
    const [isLoadingCreateFilter, setIsLoadingCreateFilter] = useState(false);
    const [expanded, setExpanded] = useState<string | false>(false);
    const [peoplePickerInvoke, setPeoplePickerInvoke] = useState<string>('');
    const [formAllCheckBox, setFormAllCheckBox] = useState<Record<string, any>>({});
    const pendingSavedFilterRef = useRef<any>(null);
    const prevTabRef = useRef<string | undefined>(accessibleFilterTab);
    const prevArchivedRef = useRef(showArchived);
    const prevSiteRef = useRef(siteSelected);
    const mountedRef = useRef(false);
    const statusesLoadedRef = useRef(false);

    const storagePrefix = useMemo(() => moduleName, [moduleName]);
    const strategy: ModuleFilterStrategy = filterConfig?.strategy ?? defaultFilterStrategy;
    const {
        loadFromStorage,
        saveActiveFilter,
        saveFormData,
        clear: clearPersistedFilterState,
    } = useFilterPanelPersistence({
        storagePrefix,
        persistState,
        showArchived,
        activeTab: accessibleFilterTab,
    });

    const getTabStatusOverlay = useCallback(
        (tab: string | undefined): Record<string, any> => tab ? strategy.getTabStatusOverlay(tab, { listValues }) : {},
        [strategy, listValues]
    );


    const normalizeGridRows = useCallback((response: any): any[] => {
        const payload = response?.data;
        const responseDataKey = filterConfig["responseDataKey"];

        if (responseDataKey && Array.isArray(payload?.[responseDataKey])) {
            return payload[responseDataKey];
        }
        if (Array.isArray(payload)) return payload;
        if (Array.isArray(payload?.data)) return payload.data;
        if (Array.isArray(payload?.rows)) return payload.rows;
        if (Array.isArray(payload?.records)) return payload.records;
        if (Array.isArray(payload?.splot_data)) return payload.splot_data;
        if (Array.isArray(payload?.splots)) return payload.splots;
        if (Array.isArray(payload?.trial_data)) return payload.trial_data;
        if (Array.isArray(payload?.materials)) return payload.materials;
        return [];
    }, [filterConfig]);

    const { mutate: createSPLotFilter } = useCreateFilter({
        mutation: {
            onSuccess: (response: any) => {
                const apiFilterName = response?.data?.filter_name ?? formData["filter_name"] ?? "";
                const apiMessage = response?.data?.message;
                const successMessage = [apiFilterName, apiMessage]
                    .filter((part: any) => typeof part === 'string' && part.trim().length > 0)
                    .join(' ');

                showSnackbar({ message: successMessage || 'Filter created successfully', severity: 'success' });
                getAllFilters({ user_id: globalState?.user_id_db ?? "", is_archive: showArchived, url: filterConfig["getSavesFilters"], pathParams: filterConfig["getSavesFiltersPathParams"] });
                setIsLoadingCreateFilter(false);
            },
            onError: (error) => {
                setIsLoadingCreateFilter(false);
                const detail = error?.response?.data?.detail;
                showSnackbar({ message: typeof detail === 'string' ? detail : 'Error in Creating filter', severity: 'error' });
            }
        },
    })




    const { mutate: searchFilter } = useSearchFilterData({
        mutation: {
            onSuccess: (d: any) => {
                const paginationKey = filterConfig["paginationKey"];             
                const gridRows = normalizeGridRows(d);
                setGridData(gridRows);

                if (setTotalRows && paginationKey && d?.data?.[paginationKey]) {
                    setTotalRows(d?.data?.[paginationKey]?.total_records ?? 0);
                }
            },
            onError: (error) => {
                const detail = error?.response?.data?.detail;
                showSnackbar({ message: typeof detail === 'string' ? detail : 'Error in searching filter', severity: 'error' });
                setGridData([]);
                setTotalRows && setTotalRows(0);
            }
        },
    })

    const { mutate: getAllFilters } = useGetFilters({
        mutation: {
            onSuccess: (data: any) => {
                setAllSaveFilter(data?.data ?? []);
            },
            onError: (error) => {
                setAllSaveFilter([]);
                const detail = error?.response?.data?.detail;
                showSnackbar({ message: typeof detail === 'string' ? detail : 'Error in getting all user filter Data', severity: 'error' });
            }
        },
    });

    const { mutate: deleteFilterMutate } = useDeleteFilter({
        mutation: {
            onSuccess: () => {
                showSnackbar({ message: 'Filter deleted successfully', severity: 'success' });
                getAllFilters({ user_id: globalState?.user_id_db ?? "", is_archive: showArchived, url: filterConfig["getSavesFilters"], pathParams: filterConfig["getSavesFiltersPathParams"] });
                // Reset filter name field after successful deletion
                setFormData(prev => ({ ...prev, filter_name: "" }));
            },
            onError: (error) => {
                const detail = error?.response?.data?.detail;
                showSnackbar({ message: typeof detail === 'string' ? detail : 'Error deleting filter', severity: 'error' });
            }
        },
    });

    /** Build default values for form and checkbox state from field definitions */
    const buildDefaults = useCallback(() => {
        const defaults: Record<string, any> = {};
        const checkboxDefaults: Record<string, any> = {};
        fields.forEach((field: any) => {
        defaults[field?.name] = field?.defaultValue;
            if (field.type === "select") {
                checkboxDefaults[field?.name + "_all"] = false;
            }
        });
        defaults["filter_name"] = "";
        return { defaults, checkboxDefaults };
    }, [fields]);

    /** Restore saved filter + formData from localStorage for a given prefix. Returns null if nothing saved. */
    const restoreFromStorage = useCallback((defaults: Record<string, any>): { savedFilter: any; restoredFormData: Record<string, any> } | null => {
        const savedState = loadFromStorage();
        if (!savedState?.savedActiveFilter) return null;
        const savedActiveFilter = savedState.savedActiveFilter;
        const savedFormData = savedState.savedFormData;
        const restored = (savedFormData && typeof savedFormData === 'object')
            ? restoreDateRanges({ ...defaults, ...savedFormData }, fields)
            : restoreDateRanges({
                ...defaults,
                ...mapFilterToFormData(savedActiveFilter, fields, filterNameKey, listValues),
            }, fields);
        return { savedFilter: savedActiveFilter, restoredFormData: restored };
    }, [fields, loadFromStorage, filterNameKey, listValues]);

    /** Apply nullIfEmpty field rules to a raw filter payload (e.g. saved filters that bypass createReqData) */
    const applyNullIfEmpty = useCallback((filter: any) => {
        const result = { ...filter };
        fields.forEach((field: any) => {
            const value = result?.[field.name];
            const isEmptyValue = value === '' || value === null || value === undefined || (Array.isArray(value) && value.length === 0);
            if (field?.nullIfEmpty && field?.name in result && isEmptyValue) {
                result[field.name] = null;
            }
        });
        return result;
    }, [fields]);

    const hasMeaningfulFilterValue = useCallback((value: any): boolean => {
        if (value === null || value === undefined) return false;
        if (typeof value === 'string') return value.trim().length > 0;
        if (typeof value === 'number') return value !== 0;
        if (typeof value === 'boolean') return value;
        if (value instanceof Date) return !Number.isNaN(value.getTime());
        if (Array.isArray(value)) return value.some(hasMeaningfulFilterValue);
        if (typeof value === 'object') return Object.values(value).some(hasMeaningfulFilterValue);
        return false;
    }, []);

    /** Apply a saved filter — always fetch fresh data from API, defer if siteSelected is unavailable. */
    const applySavedFilter = useCallback((savedFilter: any, skipApiCall = false) => {
        if (siteSelected) {
            const normalizedFilter = applyNullIfEmpty(savedFilter);
            if (onFilterApplied) onFilterApplied(normalizedFilter);
            if (!skipApiCall) {
                searchFilter({ data: normalizedFilter, url: filterConfig["searchFilter"] });
            }
            pendingSavedFilterRef.current = null;
        } else {
            pendingSavedFilterRef.current = savedFilter;
        }
    }, [siteSelected, onFilterApplied, searchFilter, filterConfig, applyNullIfEmpty]);

    // --- Unified state-restoration effect: mount + siteSelected retry + archived toggle + tab change ---
    useEffect(() => {
        const siteChanged = prevSiteRef.current !== siteSelected;
        const prevTab = prevTabRef.current;
        const prevArchived = prevArchivedRef.current;
        const prevSite = prevSiteRef.current;
        prevTabRef.current = accessibleFilterTab;
        prevArchivedRef.current = showArchived;
        prevSiteRef.current = siteSelected;


        const isMount = !mountedRef.current;
        const tabChanged = !isMount && prevTab !== accessibleFilterTab;
        const archivedToActive = !isMount && prevArchived && !showArchived;
        const activeToArchived = !isMount && !prevArchived && showArchived;
        const archivedChanged = archivedToActive || activeToArchived;
        const siteJustArrived = !isMount && !prevSite && !!siteSelected;

        if (isMount) mountedRef.current = true;

        // (A) Deferred retry: siteSelected just became available with a pending filter
        if (siteJustArrived && !tabChanged && !archivedChanged) {
            if (!showArchived && pendingSavedFilterRef.current) {
                // If parent owns the fetch, just notify it; otherwise do the direct search.
                if (onRefreshData) {
                    applySavedFilter(pendingSavedFilterRef.current, true);
                    onRefreshData();
                } else {
                    applySavedFilter(pendingSavedFilterRef.current);
                }
                pendingSavedFilterRef.current = null;
            } else if (!showArchived) {
                // No pending saved filter — site just arrived with no saved state.
                // Trigger the default accessible filter (In Progress / Processing) load.
                if (onRefreshData) onRefreshData();
                else setInvokeSearch(true);
            }
            return;
        }

        // (B) Ignore non-transition re-renders (e.g. callback identity changes)
        // to avoid repeated restore/reset that can re-trigger filter API calls.
        const { defaults, checkboxDefaults } = buildDefaults();
        const tabOverlay = getTabStatusOverlay(accessibleFilterTab);

        if (siteChanged) {
            if (!["inSeasonTrials"].includes(moduleName)) {
                commitFormData({ ...defaults, ...tabOverlay });
            }
            else {
                commitFormData({ ...defaults });
            }
        }
       

        if (!isMount && !tabChanged && !archivedChanged) {
            // Plain site change with no other transition: trigger one fetch via parent
            // (or invoke direct search). Without this, signal-driven grids would miss
            // the site change since GenericFilterPanel previously early-returned here.
            if (siteChanged && !isMount) {
                if (onRefreshData) onRefreshData();
                else if (siteSelected) setInvokeSearch(true);
            }
            return;
        }

        // --- Common setup ---


        if (isMount || tabChanged || archivedChanged) {
            setFormAllCheckBox(checkboxDefaults);
        }
        if (tabChanged || archivedChanged) {
            setCurrentFilterSelected(-1);
        }

        // (C) Switching to archived mode: reset form to blank and trigger a fresh data load
        if (showArchived) {
            commitFormData({ ...defaults });
            pendingSavedFilterRef.current = null;
            if (onFilterApplied) onFilterApplied(null);
            if (onRefreshData) {
                onRefreshData();
            } else if (siteSelected) {
                setInvokeSearch(true);
            }
            return;
        }

        // (D) Try to restore from localStorage
        // Tab transitions:
        //  - To a non-empty tab  → apply the new tab's defaults + status overlay (do NOT
        //    restore the module-scoped saved filter, which may belong to a different tab).
        //  - To an empty tab ('' = pill unselected because all filter values are null) →
        //    no-op. The chip-clear search has already updated the data; we should not
        //    restore stale state nor fire an extra refresh.
        if (tabChanged && !accessibleFilterTab) {
            return;
        }
        const isTabReset = tabChanged && !!accessibleFilterTab;
        const saved = isTabReset ? null : restoreFromStorage(defaults);

        if (saved) {
            commitFormData(saved.restoredFormData);
            // Always notify parent of the saved filter without firing the panel's own
            // search API. When a parent owns the fetch (onRefreshData), it is the single
            // source of truth and will issue the correct (archived-aware) request.
            if (onRefreshData) {
                applySavedFilter(saved.savedFilter, true); // skip direct API
                onRefreshData();
            } else if (isMount) {
                applySavedFilter(saved.savedFilter, true);
            } else {
                applySavedFilter(saved.savedFilter);
            }
        } else {
            const nextDefaultFormData = { ...defaults, ...tabOverlay };
            commitFormData(nextDefaultFormData);
            pendingSavedFilterRef.current = null;
            saveFormData(nextDefaultFormData);
            saveActiveFilter(nextDefaultFormData);

            // Only notify parent when default state has meaningful values.
            // This avoids clearing the selected accessible pill when statuses are still loading
            // and tab overlay arrays are temporarily empty.
            const normalizedDefault = applyNullIfEmpty(nextDefaultFormData);
            if (onFilterApplied && hasMeaningfulFilterValue(normalizedDefault)) {
                onFilterApplied(normalizedDefault);
            }
            
            if (tabChanged || archivedChanged) {
                if (onRefreshData) onRefreshData();
                else if (siteSelected) setInvokeSearch(true);
            } else if (isMount && siteSelected) {
                // On mount, trigger data refresh
                if (onRefreshData) onRefreshData();
                else if (persistState) setInvokeSearch(true);
            }
        }
    }, [fields, showArchived, siteSelected, accessibleFilterTab, buildDefaults, getTabStatusOverlay, onFilterApplied, onRefreshData, restoreFromStorage, storagePrefix, applySavedFilter, moduleName, persistState, saveActiveFilter, saveFormData, applyNullIfEmpty, hasMeaningfulFilterValue, commitFormData]);

    // Re-apply tab overlay when statuses load for the first time (fixes empty chips on mount)
    useEffect(() => {
        const statuses = listValues?.statuses ?? listValues?.trialStatus ?? [];
        const hasStatuses = Array.isArray(statuses) && statuses.length > 0;
        
        // Skip if already loaded once, or still loading, or in archived mode
        if (statusesLoadedRef.current || !hasStatuses || showArchived) return;
        
        statusesLoadedRef.current = true;
        
        // Check if current formData has empty status_id (from initial load before statuses were ready)
        const statusField = fields.find((f: any) => f.name === 'status_id' || f.name === 'trial_status_id');
        if (!statusField) return;
        
        const currentStatusIds = formData?.[statusField.name];
        const hasStatusIds = Array.isArray(currentStatusIds) && currentStatusIds.length > 0;
        
        // If already has status IDs, don't override (user might have changed)
        if (hasStatusIds) return;
        
        // Re-apply tab overlay with now-available statuses
        const tabOverlay = getTabStatusOverlay(accessibleFilterTab);
        const overlayStatusIds = tabOverlay?.status_id ?? tabOverlay?.trial_status_id;
        
        if (Array.isArray(overlayStatusIds) && overlayStatusIds.length > 0) {
            const corrected = { ...formData, ...tabOverlay };
            commitFormData(corrected);
            // Persist the corrected status IDs so the next session load
            // does not restore the stale empty-array version saved at mount time.
            saveActiveFilter(corrected);
            saveFormData(corrected);
        }
    }, [listValues, showArchived, fields, formData, accessibleFilterTab, getTabStatusOverlay, saveActiveFilter, saveFormData, commitFormData]);

    const handleSelectionChanged = (e: Event, fieldName: string) => {
        const peopleFromDetail: any = (e as CustomEvent).detail as PeoplePickerPerson[];
        setFormData((prev) => ({ ...prev, [fieldName]: peopleFromDetail ?? "" }));
        setPeoplePickerInvoke(peopleFromDetail);
    }

    const normalizeSelectValue = useCallback((field: any, rawValue: any) => {
        if (field?.convertToNumber) return Number(rawValue);
        const options = resolveList(field, listValues);
        const matched = options.find((item: any) => String(item?.[field?.listKey]) === String(rawValue));
        return matched ? matched[field?.listKey] : rawValue;
    }, [listValues]);

    const handleChange = useCallback((name: string, value: any, field: any) => {
        if (field?.type == "select") {
            let checked = value?.target?.checked || false;
            let checkedValue = normalizeSelectValue(field, value?.target?.value);

            if (name?.includes("all")) {
                let filterVal = resolveList(field, listValues);
                const ids = filterVal.map((item: any) => item[field?.listKey]);

                setFormAllCheckBox(prev => ({
                    ...prev,
                    [name]: checked
                }));

                const orgKey = name?.replace(/_all$/, "");
                if (checked) {
                    setFormData(prev => ({
                        ...prev,
                        [orgKey]: ids
                    }));
                }
                else {
                    setFormData(prev => ({
                        ...prev,
                        [orgKey]: []
                    }));

                }
            }
            else {
                setFormData(prev => {
                    const current = Array.isArray(prev?.[name]) ? prev[name] : [];
                    return {
                        ...prev,
                        [name]: checked
                            ? [...current, checkedValue]
                            : current.filter((v: any) => v !== checkedValue)
                    };
                });
            }

        }
        else if (field?.type == "dateRange") {
            setFormData((prev) => ({ ...prev, [name]: value }));
        }
        else {
            setFormData((prev) => ({ ...prev, [name]: value }));
        }

    }, [setFormData, normalizeSelectValue]);

    const resetFormFields = useCallback(() => {
        const { defaults: resetData } = buildDefaults();

        // For modules with tabs (splot/trials), preserve the tab-specific fields
        if (accessibleFilterTab) {
            const tabOverlay = getTabStatusOverlay(accessibleFilterTab);
            Object.assign(resetData, tabOverlay);
        }

        setFormData(resetData);
        setAppliedFormData(resetData);
        setCurrentFilterSelected(-1);
        clearPersistedFilterState();
        if (onFilterApplied) {
            onFilterApplied(null);
        }
        if (onRefreshData) {
            onRefreshData();
        } else {
            setInvokeSearch(true);
        }
    }, [buildDefaults, onRefreshData, onFilterApplied, moduleName, accessibleFilterTab, getTabStatusOverlay, storagePrefix, persistState, clearPersistedFilterState]);

    const handleSearch = useCallback(() => {
        if (!siteSelected) return false;
        let req = createReqData();
        setFilterNameValidMsg("");
        if (req["ValidationError"].length) {
            return false;
        }
        delete req["created_by"];
        delete req["ValidationError"];
        delete req[filterConfig["filter_name_key"]];

        // Save the original request before augmentation for proper restoration
        const originalReq = { ...req };

        const ctx: FilterRequestContext = { showArchived, siteSelected, paginationModel, userId: globalState?.user_id_db, extraContext };
        req = strategy.augmentRequest(req, ctx);

        if (onFilterApplied) {
            // Notify parent with the user-driven filter only (pre-augment). System
            // fields injected by augmentRequest (site_code, is_archive, page, ...)
            // would otherwise make the parent see this as a "non-empty" filter even
            // when every chip has been cleared, preventing accessible-pill unselect.
            onFilterApplied(originalReq);
        }
        saveFormData(formData);
        saveActiveFilter(originalReq);  // Save original request (before augmentation) for proper restoration
        setAppliedFormData(formData);
        // Merge extraBody into the API request after notifying chips (so extraBody never appears as a chip)
        const finalReq = (extraBody && Object.keys(extraBody).length > 0) ? { ...req, ...extraBody } : req;
        searchFilter({ data: finalReq, url: filterConfig["searchFilter"] })
        return true;
    }, [formData, showArchived, fields, globalState, moduleName, searchFilter, filterConfig, paginationModel, onFilterApplied, strategy, siteSelected, storagePrefix, saveFormData, saveActiveFilter, extraBody]);

    const handleApplyFilters = useCallback(() => {
        if (handleSearch()) setFilterVisible(false);
    }, [handleSearch, setFilterVisible]);

    // "filter_name" only feeds the Save Quick Filter box, so it must not enable Apply.
    const hasUnappliedChanges = useMemo(() => {
        const serialize = (data: Record<string, any>) => JSON.stringify(
            fields.map((field: any) => data?.[field?.name] ?? null)
        );
        return serialize(formData) !== serialize(appliedFormData);
    }, [formData, appliedFormData, fields]);

    // Re-run handleSearch when the parent increments searchTrigger (e.g. search button with active filters)
    const handleSearchRef = useRef(handleSearch);
    handleSearchRef.current = handleSearch;
    useEffect(() => {
        if (!searchTrigger) return;
        handleSearchRef.current();
    }, [searchTrigger]);

    const createFilter = useCallback(() => {

        let req = createReqData();
        setFilterNameValidMsg("");
        if (!formData["filter_name"]) {
            setFilterNameValidMsg("Please Provide filter name");
            return;
        }
        if (req["ValidationError"].length) {
            return;
        }
        delete req["ValidationError"];
        setIsLoadingCreateFilter(true);
        createSPLotFilter({ data: req, url: filterConfig["createFilter"] })
    }, [formData, showArchived, fields, globalState, moduleName, createSPLotFilter, filterConfig]);

    const createReqData = useCallback(() => {
        let req: any = {};
        setDateValidMsg("");
        let ValidationError: any = [];
        for (let c = 0; c < fields?.length; c++) {
            const field = fields[c];
            const fieldValue = formData?.[field?.name];
            const isEmptyValue = fieldValue === '' || fieldValue === null || fieldValue === undefined || (Array.isArray(fieldValue) && fieldValue.length === 0);
            if (field?.nullIfEmpty && isEmptyValue) {
                req[field?.name] = null;
                continue;
            }
            if (field['reqKey']) {

                req[field?.reqKey] = formData?.[field?.name] ? formData?.[field?.name] : ""
                if (field?.type == "dateRange") {
                    let fieldSplit = field?.reqKey?.split("-");
                    const [fromDate, toDate] = Array.isArray(formData[field?.name]) ? formData[field?.name] : [];
                    if ((!fromDate && toDate) || (fromDate && !toDate)) {
                        setDateValidMsg("Please provide valid Date range");
                        ValidationError.push("Please provide valid Date range");
                    }
                    req[fieldSplit[0]] = fromDate ? checkDate(fromDate.format('YYYY-MM-DD')) : "";
                    req[fieldSplit[1]] = toDate ? checkDate(toDate.format('YYYY-MM-DD')) : "";

                    delete req?.[field?.['reqKey']];
                }
                else if (field['type'] == "PeoplePicker") {
                    const ppVal = formData?.[field?.name];
                    req[field['reqKey']] = Array.isArray(ppVal)
                        ? ppVal
                            .map((person: any) => person?.displayName)
                            .filter((name: any) => typeof name === 'string' && name.trim().length > 0)
                        : [];
                }
                else if (field['type'] == "textMulti" || field['type'] == "text") {
                    const txtVal = formData?.[field?.name];
                    req[field?.reqKey] = txtVal ? String(txtVal).trim() : "";
                }
            }
            else {
                // For select fields with transformKey, convert selected listKey values to transformKey values
                if (field?.type === 'select' && field?.transformKey && field?.list && field?.listKey) {
                    const selectedValues = formData?.[field?.name];
                    if (Array.isArray(selectedValues) && selectedValues.length > 0) {
                        const listData = Array.isArray(field.list) ? field.list : (listValues?.[field.list] ?? []);
                        req[field.name] = selectedValues.map((val: any) => {
                            const item = listData.find((item: any) => item[field.listKey] === val);
                            return item?.[field.transformKey] ?? val;
                        });
                    } else {
                        req[field.name] = selectedValues;
                    }
                } else if (field?.type === 'radio') {
                    // Radio fields: preserve boolean values (true/false), handle removeReqKeyEmpty
                    const radioVal = formData?.[field?.name];
                    if (field?.removeReqKeyEmpty && (radioVal === null || radioVal === undefined || radioVal === '')) {
                        // Skip adding this field to request if removeReqKeyEmpty is set and value is empty
                        continue;
                    }
                    req[field.name] = radioVal;
                } else if (field?.type === 'number') {
                    // Number fields: TextField returns a string; coerce to Number so the
                    // API receives a numeric type instead of a string. Empty/null values
                    // are already handled above by the nullIfEmpty branch (or pass through
                    // unchanged when nullIfEmpty isn't set).
                    const numVal = formData?.[field?.name];
                    if (numVal === '' || numVal === null || numVal === undefined) {
                        req[field.name] = numVal;
                    } else {
                        const parsed = Number(numVal);
                        req[field.name] = Number.isFinite(parsed) ? parsed : numVal;
                    }
                } else {
                    req[field.name] = formData?.[field?.name];
                }
            }

        }
        req['created_by'] = globalState?.user_id_db;
        req["ValidationError"] = ValidationError;
        req[filterConfig.filter_name_key] = formData["filter_name"];

        return req;

    }, [formData, fields, globalState, filterConfig, listValues]);

    const handleDelete = useCallback((filter: any) => (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!filterConfig["deleteFilter"]) return;
        deleteFilterMutate({
            data: {
                user_id: globalState?.user_id_db,
                filter_name: filter[filterNameKey],
                filter_id: filter[filterConfig["filter_id_key"] ?? "id"],
                pathKey: filterConfig["deleteFilterPathKey"],
                mode: filterConfig["deleteFilterMode"]
            },
            url: filterConfig["deleteFilter"]
        });

    }, [deleteFilterMutate, globalState?.user_id_db, filterNameKey, filterConfig]);

    const onSelectFilter = useCallback((index: number, filter: any) => {
        if (currentFilterSelected == index) return;
        setCurrentFilterSelected(index);
        const initialData = mapFilterToFormData(filter, fields, filterConfig["filter_name_key"], listValues);
        // Selecting a quick filter must not seed the "Save Quick Filter" name box.
        setFormData({ ...initialData, filter_name: "" });
    }, [fields, filterConfig, currentFilterSelected, listValues]);

    useEffect(() => {

        globalState?.user_id_db && filterConfig["getSavesFilters"] && getAllFilters({ user_id: globalState?.user_id_db, is_archive: showArchived, url: filterConfig["getSavesFilters"], pathParams: filterConfig["getSavesFiltersPathParams"] });
    }, [showArchived, globalState?.user_id_db]);

    useEffect(() => {
        if (!invokeSearch) return;
        handleSearch();
        setInvokeSearch(false);
    }, [invokeSearch, handleSearch]);

    useEffect(() => {
        // Sync "Select All" checkbox state (batch into single update)
        const checkboxUpdates: Record<string, boolean> = {};
        fields.forEach((field: any) => {
            if (field.type === "select") {
                const filterVal = resolveList(field, listValues);
                checkboxUpdates[field.name + "_all"] = filterVal?.length > 0 && filterVal?.length === formData?.[field?.name]?.length;
            }
        });
        if (Object.keys(checkboxUpdates).length > 0) {
            setFormAllCheckBox(prev => ({ ...prev, ...checkboxUpdates }));
        }

        // Build filter chip descriptors
        setFilterFieldData(buildChipDescriptors(appliedFormData, fields, listValues));
    }, [formData, appliedFormData, fields, listValues, setFilterFieldData]);

    // Persist form data to localStorage separately (debounced to avoid excessive writes)
    useEffect(() => {
        if (!persistState || showArchived || Object.keys(formData).length === 0) return;
        const timer = setTimeout(() => {
            //saveFormData(formData);
        }, 300);
        return () => clearTimeout(timer);
    }, [formData, storagePrefix, showArchived, persistState, saveFormData]);

    const isChecked = (checkedValue: string | number, field: any) => {
        checkedValue = normalizeSelectValue(field, checkedValue);
        const val = formData?.[field?.name];
        return Array.isArray(val) ? val.includes(checkedValue) : false;
    }

    const tabChange =
        (panel: string) => (_e: React.SyntheticEvent, isExpanded: boolean) =>
            setExpanded(isExpanded ? panel : false);



    useEffect(() => {
        if (!filtetToDelete?.name) return;
        const fieldDef = fields.find((f: any) => f.name === filtetToDelete.name);
        setFormData((prev) => ({
            ...prev,
            [filtetToDelete.name]: getFieldResetValue(filtetToDelete.type, fieldDef?.defaultValue),
        }));
        setInvokeSearch(true);
    }, [filtetToDelete]);

     useEffect(() => {
         setCurrentFilterSelected(-1);
        // Drop edits that were never applied so the panel reopens in the applied state.
        if (!isFilterVisible) {
            setFormData((prev) => (Object.keys(appliedFormDataRef.current).length ? appliedFormDataRef.current : prev));
        }
    }, [isFilterVisible]);

    return (
        <>
            <Drawer
                open={isFilterVisible}
                sx={drawerSx}
                variant="temporary"
                anchor="right"
                ModalProps={{ keepMounted: false, BackdropProps: { sx: drawerBackdropSx } }}>

                <Box sx={{ flexShrink: 0 }}>
                    <Typography component="div" sx={{ ml: "30px", mt: "15px" }} className={classes.labelNoto20Bold500}>
                        Filters
                    </Typography>
                    <IconButton aria-label="close" disableRipple className={classes.filterCloseButton} onClick={() => setFilterVisible(false)}>
                        <CloseIcon sx={{ fontSize: 30 }} />
                    </IconButton>
                    <Divider sx={{ mt: 2 }} />
                </Box>

                <Box sx={{ flex: 1, minHeight: 0, width: '100%', px: "25px", pb: 2, overflowY: "auto" }}>
                    {allSavedFilters.length > 0 && (
                        <Typography component="h6" sx={{ mt: "20px" }} className={classes.labelNoto14Bold400}>Quick Filters</Typography>
                    )}
                    <Stack direction="row" flexWrap="wrap" alignItems="flex-start"
                        sx={{ mt: allSavedFilters.length ? 2 : 0, gap: 1, alignContent: 'flex-start' }}>
                        {allSavedFilters?.map((filter, index) => (
                            <Chip
                                key={filter?.id ?? `${filter?.[filterNameKey]}-${index}`}
                                size="small"
                                label={filter?.[filterNameKey] ?? ""}
                                title={filter?.[filterNameKey] ?? ""}
                                deleteIcon={<CloseIcon />}
                                onClick={() => onSelectFilter(index, filter)}
                                onDelete={handleDelete(filter)}
                                className={classes.filerChip}
                                variant="outlined"
                                sx={chipSx(currentFilterSelected === index)}
                            />
                        ))}
                    </Stack>

                    <Box sx={{ display: 'flex', flexDirection: "row", gap: 1, width: "100%", mt: allSavedFilters.length > 0 ? "16px" : "12px" }}>
                        <FormControl sx={{ display: "flex", flexDirection: "column", width: "100%" }} size="small">
                            <FormLabel className={classes.labelNoto14Bold400} sx={{ color: black }}>
                                Save Quick Filter
                            </FormLabel>
                            <TextField variant="outlined" placeholder="Save current filter as.."
                                value={formData["filter_name"] ?? ""} onChange={(e) => handleChange("filter_name", e.target.value, null)}
                                sx={(theme) => ({
                                    mb: 1, mt: 1,
                                    ...MtComStyleObj(theme).textFocusHoverStyle,
                                    ...MtComStyleObj(theme).textBoxValueStyle,
                                    ...MtComStyleObj(theme).placeHolderStyle,
                                })}
                                slotProps={{
                                    input: { sx: { height: 40, width: { xs: '100%', md: '100%', sm: '100%' } } },
                                    formHelperText: { sx: { ml: 0, mt: 0 } },
                                }} />
                            <FormHelperText sx={{ ml: 0, mt: 0 }} className={classes.errorText}>{filterNameValidMsg}</FormHelperText>
                        </FormControl>
                        <Box sx={{ mt: 3.5 }}>
                            <Button variant="contained" disableRipple loading={isLoadingCreateFilter} loadingPosition="start"
                                sx={(theme) => ({ ...MtComStyleObj(theme).disabledWhiteBtnStyle, padding: "10px 12px" })}
                                className={classes.greenButton} onClick={createFilter}>
                                Save
                            </Button>
                        </Box>
                    </Box>

                    <Stack direction="column" sx={{ mt: 1, width: "100%" }}>
                        {sortedFields?.map((field: any) => (
                            <Accordion
                                key={'panel' + field?.label}
                                expanded={expanded === field?.name}
                                onChange={tabChange(field?.name)}
                                sx={(theme) => ({
                                    ...MtComStyleObj(theme).filterAccordianStyle,
                                    borderTop: `1px solid ${theme.palette.divider}`,
                                    pl: 0, padding: '0px 0px',
                                    '&.MuiAccordion-root.Mui-expanded': { marginTop: 0 },
                                    '&.MuiAccordionDetails-root': { p: 0 },
                                })}>
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon sx={{ color: black }} />}
                                    sx={accordionSummaryExpandedSx}
                                    aria-controls="panel1-content">
                                    <FormLabel className={classes.labelNoto14Bold400} sx={{ color: black }}>
                                        {field.label} {
                                            field.type === "select" && (formData[field.name] ?? []).length > 0
                                                ? `(${(formData[field.name] ?? []).length})`
                                                : field.type === "textMulti" && typeof formData[field.name] === 'string' && formData[field.name].trim().length > 0
                                                    ? `(${formData[field.name].split(',').map((s: string) => s.trim()).filter((s: string) => s.length > 0).length})`
                                                    : (field.type === "text" || field.type === "number") && formData[field.name] !== undefined && formData[field.name] !== null && String(formData[field.name]).trim() !== ''
                                                        ? `(1)`
                                                        : field.type === "radio" && formData[field.name] !== undefined && formData[field.name] !== null && formData[field.name] !== ''
                                                            ? `(1)`
                                                            : field.type === "dateRange" && Array.isArray(formData[field.name]) && formData[field.name].some((d: any) => d !== null && d !== undefined)
                                                                ? `(1)`
                                                                : ""
                                        }
                                    </FormLabel>
                                </AccordionSummary>
                                <AccordionDetails sx={{ width: '100%', pl: 0, border: "none", outline: "none" }}>
                                    <FilterFieldRenderer
                                        field={field}
                                        formData={formData}
                                        formAllCheckBox={formAllCheckBox}
                                        listValues={listValues}
                                        dateValidMsg={dateValidMsg}
                                        peoplePickerInvoke={peoplePickerInvoke}
                                        handleChange={handleChange}
                                        isChecked={isChecked}
                                        handleSelectionChanged={handleSelectionChanged}
                                    />
                                </AccordionDetails>
                            </Accordion>
                        ))}
                    </Stack>
                </Box>

                <Box sx={{ flexShrink: 0, borderTop: 1, borderColor: 'divider', px: "25px", py: "16px", display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                    <Button variant="contained" disableRipple className={classes.whiteButton}
                        onClick={resetFormFields} sx={{ padding: "10px 12px" }}>
                        Clear Filters
                    </Button>
                    <Button variant="contained" disableRipple className={classes.greenButton}
                        disabled={!hasUnappliedChanges}
                        onClick={handleApplyFilters}
                        sx={(theme) => ({ ...MtComStyleObj(theme).disabledWhiteBtnStyle, padding: "10px 12px" })}>
                        Apply Filters
                    </Button>
                </Box>
            </Drawer>
        </>
    );
}

export default memo(GenericFilterPanel);
